import React, { useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Building2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { BookingServiceResponseDto, MeetingType } from '../../types/booking';
import { usePublicServices, usePublicAvailability, usePublicBookingSettings, useCreatePublicBooking } from '../../hooks/usePublicBooking';
import BookingStepper from './BookingStepper';
import BookingConfirmation from './BookingConfirmation';
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../RecaptchaCheckbox';

interface Props {
  bookingSlug: string;
}

const STEPS = ['Service', 'Date', 'Time', 'Details', 'Confirmation'];

const DEFAULT_COLORS = {
  companyName: 'Strivre Services',
  logoUrl: '',

  primaryColor: '#172554',
  secondaryColor: '#e2e8f0',
  buttonColor: '#7f1d1d',
  accentColor: '#be123c',
};

// Reusable, embeddable public booking widget (desktop / tablet / mobile responsive).
export default function BookingWidget({ bookingSlug }: Props) {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState<BookingServiceResponseDto | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [customer, setCustomer] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '' });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { data: branding } = usePublicBookingSettings(bookingSlug);

  const colors = {
    companyName: branding?.companyName?.trim() || DEFAULT_COLORS.companyName,
    logoUrl: branding?.logoUrl?.trim() || DEFAULT_COLORS.logoUrl,
    primaryColor: branding?.primaryColor?.trim() || DEFAULT_COLORS.primaryColor,
    secondaryColor: branding?.secondaryColor?.trim() || DEFAULT_COLORS.secondaryColor,
    buttonColor: branding?.buttonColor?.trim() || DEFAULT_COLORS.buttonColor,
    accentColor: branding?.accentColor?.trim() ||  DEFAULT_COLORS.accentColor,
  };

  const { data: services, isLoading: loadingServices } = usePublicServices(bookingSlug);
  console.log('services', services)
  const { startDate, endDate } = useMemo(() => {
    if (!selectedDate) return { startDate: '', endDate: '' };
    return { startDate: `${selectedDate}T00:00:00Z`, endDate: `${selectedDate}T23:59:59Z` };
  }, [selectedDate]);

  const { data: availability, isLoading: loadingSlots } =
  usePublicAvailability(
    bookingSlug,
    selectedService?.id ?? '',
    startDate,
    endDate
  );
  const slots = availability?.[0]?.slots ?? [];
  const timezone = availability?.[0]?.timezone ?? 'UTC';
  const createBooking = useCreatePublicBooking();

  const goNext = () => setStep((s) => Math.min(STEPS.length, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleConfirm = () => {
    if (!selectedService || !selectedTime) return;
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }
    setCaptchaError('');
    createBooking.mutate(
      {
        bookingServiceId: selectedService.id,
        customerFirstName: customer.firstName,
        customerLastName: customer.lastName,
        customerEmail: customer.email,
        customerPhone: customer.phone,
        customerNotes: customer.notes,
        bookingDate: selectedTime,
        captchaToken,
      },
      {
        onSuccess: () => goNext(),
        onError: (error) => {
          const response = (error as { response?: { data?: { code?: string; message?: string } } })?.response;
          toast.error(response?.data?.code === 'CAPTCHA_FAILED'
            ? 'Please complete the CAPTCHA verification.'
            : response?.data?.message || 'Unable to complete your booking. Please try again.');
        },
        onSettled: () => {
          setCaptchaToken(null);
          recaptchaRef.current?.reset();
        },
      }
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="bg-white rounded-3xl shadow-xl border border-strivre-lightnavy overflow-hidden transition-all duration-500 hover:shadow-2xl">
        <div
          style={{ backgroundColor: colors.primaryColor }}
          className="px-6 py-8 text-white"
        >
          <div className="flex flex-col items-center text-center">

            {colors.logoUrl ? (
              <img
                src={colors.logoUrl}
                alt={colors.companyName ?? 'Company Logo'}
                className="max-h-20 w-auto object-contain mb-2"
              />
            ) : 
            <div style={{ backgroundColor: 'var(--newsletter-primary)' }} className="flex h-14 w-14 items-center justify-center rounded-2xl text-white mb-2">
              <Building2 size={36} />
            </div>
            }

            <h1 className="text-3xl font-bold text-white">
              {colors.companyName}
            </h1>

            <p className="mt-2 text-white/80">
              Book an appointment with our team. Select a service, choose a convenient time, and complete your reservation.
            </p>

          </div>
        </div>

        <div className="grid lg:grid-cols-3">
          {/* Summary Panel */}
          <div style={{ backgroundColor: colors.secondaryColor }} className="p-6 border-r border-strivre-lightnavy transition-all duration-300">
            <h2 className="text-lg font-semibold text-strivre-navy mb-6">
              Booking Summary
            </h2>

            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                  Service
                </p>
                <p className="font-medium text-strivre-navy transition-all duration-300">
                  {selectedService?.name || "Not selected"}
                </p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                  Duration
                </p>
                <p className="font-medium text-strivre-navy transition-all duration-300">
                  {selectedService
                    ? `${selectedService.durationMinutes} minutes`
                    : "-"}
                </p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                  Date
                </p>
                <p className="font-medium text-strivre-navy transition-all duration-300">
                  {selectedDate || "-"}
                </p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                  Time
                </p>
                <p className="font-medium text-strivre-navy transition-all duration-300">
                  {selectedTime
                    ? new Date(selectedTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: timezone
                      })
                    : "-"}
                </p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                  Meeting Type
                </p>

                <p className="font-medium text-strivre-navy">
                  {!selectedService
                    ? "-"
                    : selectedService.meetingType === MeetingType.Online
                      ? "Online"
                      : selectedService.meetingType === MeetingType.OnSite
                        ? "On-Site"
                        : "Phone"}
                </p>
              </div>
              {selectedService?.meetingType === MeetingType.OnSite &&
                selectedService.meetingLocation && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-strivre-mutedtext">
                      Location
                    </p>

                    <p className="font-medium text-strivre-navy">
                      {selectedService.meetingLocation}
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 p-6 md:p-8">
            <BookingStepper
              steps={STEPS}
              currentStep={step}
              primaryColor={colors.primaryColor}
              accentColor={colors.accentColor}
            />

            <div key={step} className="mt-8 animate-[slideFade_.35s_ease-out]">
              {step === 1 && (
                <div>
                  <h2 className="text-xl font-bold text-strivre-navy mb-1">
                    Select a Service
                  </h2>

                  <p className="text-sm text-strivre-mutedtext mb-6">
                    Choose the service you'd like to book.
                  </p>

                  {loadingServices && (
                    <Loader2 className="animate-spin text-strivre-navy" />
                  )}

                  <div className="grid gap-4">
                    {services?.map((svc) => (
                      <button
                        key={svc.id}
                        onClick={() => {
                          setSelectedService(svc);
                          goNext();
                        }}
                        style={
                          selectedService?.id === svc.id
                            ? { borderColor: colors.accentColor, boxShadow: `0 0 0 1px ${colors.accentColor}33` }
                            : undefined
                        }
                        className={`text-left rounded-2xl border p-5 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg ${
                          selectedService?.id === svc.id
                            ? "shadow-lg"
                            : "border-strivre-lightnavy hover:border-strivre-burgundy"
                        }`}
                      >
                        <h3 className="font-semibold text-strivre-navy">
                          {svc.name}
                        </h3>
                        <p className="text-sm text-strivre-mutedtext">
                          {svc.description}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {svc.durationMinutes} mins
                          </span>

                          <span
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: `${colors.accentColor}15`,
                              color: colors.accentColor,
                            }}
                          >
                            {svc.price != null && svc.price > 0
                              ? `$${svc.price.toFixed(2)}`
                              : "Free"}
                          </span>
                          <span
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: `${colors.primaryColor}10`,
                              color: colors.primaryColor,
                            }}
                          >
                            {svc.meetingType === MeetingType.Online
                              ? "Online"
                              : svc.meetingType === MeetingType.OnSite
                                ? "On-Site"
                                : "Phone"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="text-xl font-bold text-strivre-navy mb-1">
                    Select a Date
                  </h2>

                  <p className="text-sm text-strivre-mutedtext mb-6">
                    Choose your preferred appointment date.
                  </p>

                  <input
                    type="date"
                    value={selectedDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) =>
                      setSelectedDate(e.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 text-slate-900 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-strivre-burgundy"
                  />
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="text-xl font-bold text-strivre-navy mb-1">
                    Select a Time
                  </h2>

                  <p className="text-sm text-strivre-mutedtext mb-6">
                    Available time slots for your selected date.
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Times shown in {timezone}
                  </p>

                  {loadingSlots && (
                    <Loader2 className="animate-spin text-strivre-navy" />
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {slots.map((slot: any) => (
                      <button
                        key={slot.startTime}
                        disabled={!slot.isAvailable}
                        onClick={() =>
                          setSelectedTime(slot.startTime)
                        }
                        style={
                          slot.isAvailable && selectedTime === slot.startTime
                            ? { backgroundColor: colors.accentColor, borderColor: colors.accentColor }
                            : undefined
                        }
                        className={`rounded-xl border p-3 text-sm font-medium transition-all duration-300 ease-out hover:scale-105 ${
                          !slot.isAvailable
                            ? "bg-strivre-softgrey text-slate-400 cursor-not-allowed"
                            : selectedTime === slot.startTime
                              ? "text-white scale-105 shadow-md"
                              : "border-slate- text-slate-900 hover:border-strivre-burgundy"
                        }`}
                      >
                        <div>
                          {new Date(slot.startTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              timeZone: timezone
                          })}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="text-xl font-bold text-strivre-navy mb-1">
                    Your Details
                  </h2>

                  <p className="text-sm text-strivre-mutedtext mb-6">
                    Tell us a little about yourself.
                  </p>

                  <div className="space-y-5 text-slate-900 text-left">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          First Name
                        </label>

                        <input
                          className="input-base w-full"
                          value={customer.firstName}
                          onChange={(e) =>
                            setCustomer({
                              ...customer,
                              firstName: e.target.value
                            })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Last Name
                        </label>

                        <input
                          className="input-base w-full"
                          value={customer.lastName}
                          onChange={(e) =>
                            setCustomer({
                              ...customer,
                              lastName: e.target.value
                            })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Email Address
                      </label>

                      <input
                        type="email"
                        className="input-base w-full"
                        value={customer.email}
                        onChange={(e) =>
                          setCustomer({
                            ...customer,
                            email: e.target.value
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Phone Number
                      </label>

                      <input
                        className="input-base w-full"
                        value={customer.phone}
                        onChange={(e) =>
                          setCustomer({
                            ...customer,
                            phone: e.target.value
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Notes
                      </label>

                      <textarea
                        rows={4}
                        className="input-base w-full"
                        value={customer.notes}
                        onChange={(e) =>
                          setCustomer({
                            ...customer,
                            notes: e.target.value
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && createBooking.data && (
                <BookingConfirmation
                  booking={createBooking.data}
                  accentColor={colors.accentColor}
                  timezone={timezone}
                />
              )}
            </div>

            {step === 4 && (
              <div className="mt-6">
                <RecaptchaCheckbox ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token); setCaptchaError(''); }} disabled={createBooking.isPending} />
                {captchaError && <p className="mt-2 text-sm text-red-600">{captchaError}</p>}
              </div>
            )}

            {step < 5 && (
              <div className="flex justify-between mt-10 pt-6 border-t border-strivre-lightnavy">
                <button
                  onClick={goBack}
                  disabled={step === 1}
                  className="
                    px-4 py-2 rounded-lg border border-slate-300
                    text-strivre-navy
                    transition-all duration-300
                    hover:-translate-y-0.5
                    hover:shadow-md
                    disabled:opacity-40
                    "
                >
                  <ArrowLeft size={16} className="inline mr-2" />
                  Back
                </button>

                {step === 4 ? (
                  <button
                    onClick={handleConfirm}
                    disabled={createBooking.isPending}
                    style={{ backgroundColor: colors.buttonColor }}
                    className="
                      text-white px-6 py-3 rounded-xl
                      font-semibold
                      transition-all duration-300
                      hover:-translate-y-0.5
                      hover:shadow-lg
                      disabled:opacity-50
                      "
                  >
                    {createBooking.isPending
                      ? "Creating Booking..."
                      : "Confirm Booking"}
                  </button>
                ) : (
                  <button
                    onClick={goNext}
                    disabled={
                      (step === 2 && !selectedDate) ||
                      (step === 3 && !selectedTime)
                    }
                    style={{ backgroundColor: colors.buttonColor }}
                    className="
                      text-white px-6 py-3 rounded-xl
                      font-semibold
                      transition-all duration-300
                      hover:-translate-y-0.5
                      hover:shadow-lg
                      disabled:opacity-40
                      "
                  >
                    Next
                    <ArrowRight
                      size={16}
                      className="inline ml-2"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

